/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

let collectionRequest

const loadCollection = async (url = window.location.href, { history = true, scroll = true } = {}) => {
  collectionRequest?.abort()
  const request = new AbortController()
  collectionRequest = request
  const collectionProducts = document.querySelector('.collection-products')
  if (!collectionProducts) return
  const filters = document.querySelector('collection-filters')
  const focusedFilterId = filters?.contains(document.activeElement) ? document.activeElement.id : null
  collectionProducts.setAttribute('aria-busy', 'true')
  const productList = collectionProducts.querySelector('.product-list')
  if (productList) productList.style.opacity = '.2'

  try {
    const response = await fetch(url, { signal: request.signal })
    if (!response.ok) throw new Error(`Collection request failed: ${response.status}`)
    const newDocument = new DOMParser().parseFromString(await response.text(), 'text/html')
    if (request.signal.aborted) return
    const newProducts = newDocument.querySelector('.collection-products')
    if (!newProducts) throw new Error('Collection content missing')
    if (history) window.history.pushState({}, '', url)
    collectionProducts.replaceWith(newProducts)
    const newFilters = newDocument.querySelector('collection-filters')
    if (filters && newFilters) filters.replaceWith(newFilters)
    if (focusedFilterId) document.getElementById(focusedFilterId)?.focus({ preventScroll: true })
    window.dispatchEvent(new CustomEvent('kt.collection.loaded'))
    if (scroll) {
      const navbarHeight = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0
      newProducts.style.scrollMarginTop = `${navbarHeight}px`
      newProducts.scrollIntoView()
    }
  } catch (error) {
    if (error.name !== 'AbortError') window.location.assign(url)
  } finally {
    if (collectionRequest === request) {
      collectionProducts.removeAttribute('aria-busy')
      if (productList) productList.style.opacity = ''
    }
  }
}

const collectionUrl = (params, pathname = window.location.pathname) => {
  const query = params.toString()
  return `${pathname}${query ? `?${query}` : ''}`
}

class CollectionFilters extends HTMLElement {
  connectedCallback () {
    if (this.initialized) return
    this.initialized = true
    this.form = this.querySelector('form')
    if (!this.form) return

    const applyFilters = () => {
      const params = new URLSearchParams(window.location.search)
      for (const key of [...params.keys()]) {
        if (key.startsWith('filter.') || key === 'page' || key === 'sort_by') params.delete(key)
      }
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') {
          if (!key.startsWith('filter.')) params.delete(key)
          params.append(key, value)
        }
      }
      loadCollection(collectionUrl(params), { scroll: false })
    }

    this.form.addEventListener('change', applyFilters)
    this.form.addEventListener('submit', (event) => {
      event.preventDefault()
      applyFilters()
    })

    this.querySelectorAll('.btn-filters-clear-all').forEach(btn => {
      const hasFilters = [...new URLSearchParams(window.location.search).keys()].some(key => key.startsWith('filter.'))
      btn.hidden = !hasFilters && this.dataset.hasTagFilters !== 'true'
      btn.addEventListener('click', () => {
        const params = new URLSearchParams(window.location.search)
        for (const key of [...params.keys()]) {
          if (key.startsWith('filter.') || key === 'page') params.delete(key)
        }
        loadCollection(collectionUrl(params, this.dataset.collectionUrl || window.location.pathname))
      })
    })
  }
}
customElements.define('collection-filters', CollectionFilters)

class SortBy extends HTMLElement {
  connectedCallback () {
    if (this.initialized) return
    this.initialized = true
    this.addEventListener('change', (event) => {
      if (!event.target.matches('input[name="sort_by"]')) return
      const params = new URLSearchParams(window.location.search)
      params.set('sort_by', event.target.value)
      params.delete('page')
      loadCollection(collectionUrl(params))
    })
  }
}
customElements.define('sort-by', SortBy)

// Delegation survives replacement of the product grid and needs no inline scripts.
document.addEventListener('click', (event) => {
  const link = event.target.closest('.prf-filters a, collection-pagination a')
  if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return
  const url = new URL(link.href, window.location.href)
  if (url.origin !== window.location.origin) return
  if (link.closest('.prf-tag-navigation')) {
    url.search = window.location.search
    url.searchParams.delete('page')
  }
  event.preventDefault()
  loadCollection(`${url.pathname}${url.search}${url.hash}`)
})

window.addEventListener('popstate', () => {
  if (document.querySelector('.collection-products')) loadCollection(window.location.href, { history: false })
})
